import styled from "@emotion/styled";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import themes from "../../themes";
import { Footer, StyledDivider } from "../organisms/Footer";
import RefreshIcon from "@mui/icons-material/Refresh";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WaitingProcessDialog from "../molecules/WaitingProcessDialog";
import { ChainId } from "@usedapp/core";
import { WalletButton } from "../atoms/WalletButton";
import { ethers } from "ethers";
import ChainModal from "../molecules/ChainModal";

interface PageTemplateProps {
  readonly isConnected: boolean;
  readonly transactionStatus: any;
  readonly isPurchasing: boolean;
  readonly chainId: ChainId | undefined;
  readonly account: string | null | undefined;
  readonly buy: (...args: any[]) => Promise<void>;
  readonly tokenId?: string | number;
  readonly getTokenId?: string | number;
  readonly isOnSale?: boolean;
  readonly isSoldOut?: boolean;
}

export const PageTemplate = ({ ...props }: PageTemplateProps) => {
  const onPurchaseButtonClick = useCallback(() => {
    props.buy(props.tokenId, { value: ethers.utils.parseEther("0.03") });
  }, [props.buy, props.tokenId]);

  const gameIFrame = useMemo(
    () =>
      props.tokenId !== -1 ? (
        <iframe
          src={`https://arweave.net/3mAmpxaFL1iS5AVQ9uvwZPFaDSIooP00PuQA3m7B4QY/?s=${props.tokenId}`}
        />
      ) : (
        <ProgressWrapper>
          <CircularProgress color={"primary"} size={100} />
        </ProgressWrapper>
      ),
    [props.tokenId]
  );

  return (
    <>
      <ChainModal chainId={props.chainId} />
      <ContentDiv>
        <TitleWrapper>
          <LogoImg src={"/img/logo.png"} alt="Otaku Coin Logo" />
          <Title variant="h1">Otaku Coin Arcade Game</Title>
        </TitleWrapper>
        <DescWrapper>
          <Typography>
            Generative Art NFT game "Otaku Coin Arcade Game" is an exciting
            action game that includes a DAO army of numerous Otaku coin owners
            who fight off the giant Otaku coin "invaders" on the NFT.
          </Typography>
        </DescWrapper>
        <NftWrapper>{gameIFrame}</NftWrapper>
        <StackWrapper>
          <Stack
            direction="row"
            divider={
              <StyledVerticalDivider
                orientation="vertical"
                variant="middle"
                flexItem
                light={true}
              />
            }
            spacing={2}
          >
            <IconButton
              aria-label="reflesh"
              size="medium"
              onClick={() => window.location.reload()}
            >
              <RefreshIcon fontSize="medium" />
            </IconButton>
            <IconButton
              aria-label="zoom"
              size="medium"
              onClick={() => {
                window.open(
                  `https://arweave.net/3mAmpxaFL1iS5AVQ9uvwZPFaDSIooP00PuQA3m7B4QY/?s=${props.tokenId}`,
                  "_blank"
                );
              }}
            >
              <ZoomOutMapIcon fontSize="medium" />
            </IconButton>
          </Stack>
        </StackWrapper>
        <StyledDivider orientation="horizontal" flexItem light={true} />
        <PriceWrapper>
          <PriceTypo>
            0.03<span>ETH</span>
          </PriceTypo>
          {props.isConnected ? (
            props.isSoldOut ? (
              <Button
                variant="contained"
                size="large"
                onClick={() =>
                  window.open(
                    "https://opensea.io/collection/otaku-coin-arcade-game",
                    "_blank"
                  )
                }
              >
                View on OpenSea
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={onPurchaseButtonClick}
                disabled={!props.isOnSale}
              >
                PURCHASE
              </Button>
            )
          ) : (
            <WalletButton account={props.account} />
          )}
        </PriceWrapper>
        <Coution>
          <span>
            <ErrorOutlineIcon />
          </span>
        </Coution>
        <UlWrapper>
          <ul>
            <li>This game can be purchased as an NFT on Ethereum. </li>
            <li>The total number of games we have for sales is 666.</li>
            <li>The price is 0.03ETH. (ALL)</li>
            <li>
              The purchase button will allow you to buy the currently displayed
              game as an NFT.
            </li>
            <li>
              There is no rarity setting for this NFT, and the game difficulty
              has no relation to it.
            </li>
            <li>
              We have 3 difficulty levels for the game. (EASY, NORMAL, HARD)
            </li>
            <li>
              You can play different games in terms of the difficulty level and
              the background color by reloading the webpage.
            </li>
            <li>
              The game is licensed under CC BY-NC-SA 4.0 which means it can be
              only used for non-commercial purposes.
            </li>
          </ul>
        </UlWrapper>
        <Prof>
          <IconImg src={"/img/icon.png"} />
          <ProfTextWrapper>
            <Name>ikeryou</Name>
            <Typography>
              池田亮 / devdev Inc. Founder / Creative Coder / Programmer / web /
              富山
            </Typography>
            <Typography>
              <Link
                href="https://devdev.jp/"
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                devdev.jp
              </Link>{" "}
              |{" "}
              <Link
                href="https://twitter.com/ikeryou"
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                Twitter
              </Link>{" "}
              |{" "}
              <Link
                href="https://ikeryou.hatenablog.com/archive/category/%E4%BB%95%E4%BA%8B"
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                Portfolio
              </Link>
            </Typography>
          </ProfTextWrapper>
        </Prof>
      </ContentDiv>
      <Footer />
      <WaitingProcessDialog
        account={props.account}
        transactionStatus={props.transactionStatus}
        isPurchasing={props.isPurchasing}
        chainId={props.chainId}
        getTokenId={props.getTokenId}
      />
    </>
  );
};

const ContentDiv = styled.div`
  padding-top: ${themes.spacing(8)};
  padding-left: 1rem;
  padding-right: 1rem;
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: ${themes.spacing(10)};
`;

const TitleWrapper = styled.div`
  text-align: center;
  margin-bottom: ${themes.spacing(2)};
`;

const Title = styled(Typography)`
  font-family: "Press Start 2P", cursive;
  font-size: 30px;
  padding-top: 16px;
`;

const LogoImg = styled.img`
  max-width: 60px;
  width: 100%;
`;

const DescWrapper = styled.div`
  margin-bottom: ${themes.spacing(4)};
`;

const NftWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;

  & > iframe {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const StackWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${themes.spacing(1)};
  padding: 8px 0 4px;
`;

const PriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 6px;

  @media screen and (max-width: 420px) {
    display: block;
    text-align: center;
    & > button {
      width: 100%;
      margin-top: 8px;
    }
  }
`;

const PriceTypo = styled(Typography)`
  font-family: "Press Start 2P", cursive;
  font-size: 34px;
  & > span {
    font-size: 18px;
    padding-left: 8px;
  }
`;

const StyledVerticalDivider = styled(Divider)`
  border-color: #fff;
  height: 30px;
  align-self: center;
`;

const Coution = styled.div`
  display: flex;
  &::before,
  &::after {
    content: "";
    position: relative;
    width: 100%;
    border-top: thin solid #fff;
    top: 50%;
    transform: translateY(50%);
  }
  & > span {
    display: flex;
    padding: 0 8px;
  }
`;

const UlWrapper = styled.div`
  margin-bottom: ${themes.spacing(3)};
`;

const Prof = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border: 1px solid #36cf00;
  &::after {
    font-family: "Press Start 2P", cursive;
    content: "CREATOR";
    display: block;
    font-size: 12px;
    color: #000;
    background-color: #36cf00;
    position: absolute;
    right: 0;
    top: 0;
    padding: 4px 16px;
  }

  @media screen and (max-width: 420px) {
    display: block;
    text-align: center;
  }
`;

const ProfTextWrapper = styled.div`
  padding-left: 16px;
  @media screen and (max-width: 420px) {
    text-align: left;
  }
`;

const IconImg = styled.img`
  width: 100%;
  max-width: 100px;
  height: auto;
  max-height: 100px;
`;

const Name = styled(Typography)`
  font-size: 18px;
  font-family: "Press Start 2P", cursive;
  color: #36cf00;
  margin-bottom: ${themes.spacing(1)};
`;

const ProgressWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
